import { useEffect, useState } from "react";
import api, { errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import ZButton from "@/components/ZButton";
import Modal from "@/components/Modal";
import { ZInput } from "@/components/ZInput";
import VerifiedBadge from "@/components/VerifiedBadge";
import { Ban, Check, Trash2, Search, BadgeCheck, ShieldCheck, FileText, Clock, X } from "lucide-react";

const ROLES = [
  { id: "", label: "Tous" },
  { id: "buyer", label: "Acheteurs" },
  { id: "organizer", label: "Organisateurs" },
  { id: "agent", label: "Agents" },
  { id: "admin", label: "Admins" },
];

export default function AdminUsersPage() {
  const { user: me } = useAuth();
  const [view, setView] = useState("users"); // 'users' | 'verifications'
  const [users, setUsers] = useState([]);
  const [verifs, setVerifs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [role, setRole] = useState("");
  const [q, setQ] = useState("");
  const [confirmDel, setConfirmDel] = useState(null);
  const [rejectTarget, setRejectTarget] = useState(null);
  const [rejectReason, setRejectReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const load = () => {
    setLoading(true);
    if (view === "verifications") {
      api.get("/admin/verifications", { params: { status: "pending" } })
        .then((r) => setVerifs(r.data))
        .finally(() => setLoading(false));
    } else {
      const params = {};
      if (role) params.role = role;
      api.get("/admin/users", { params })
        .then((r) => setUsers(r.data))
        .finally(() => setLoading(false));
    }
  };
  useEffect(load, [role, view]);

  const toggleSuspend = async (u) => {
    setErr("");
    try {
      await api.patch(`/admin/users/${u.id}`, { suspended: !u.suspended });
      load();
    } catch (e) {
      setErr(errMsg(e));
    }
  };

  const toggleVerified = async (u) => {
    setErr("");
    try {
      await api.patch(`/admin/users/${u.id}`, { verified: !u.verified });
      load();
    } catch (e) {
      setErr(errMsg(e));
    }
  };

  const approveVerif = async (v) => {
    setBusy(true);
    try {
      await api.post(`/admin/verifications/${v.user_id}/approve`);
      load();
    } catch (e) { setErr(errMsg(e)); } finally { setBusy(false); }
  };

  const rejectVerif = async () => {
    if (!rejectTarget) return;
    setBusy(true);
    try {
      await api.post(`/admin/verifications/${rejectTarget.user_id}/reject`, { reason: rejectReason.trim() });
      setRejectTarget(null);
      setRejectReason("");
      load();
    } catch (e) { setErr(errMsg(e)); } finally { setBusy(false); }
  };

  const remove = async () => {
    setErr("");
    try {
      await api.delete(`/admin/users/${confirmDel.id}`);
      setConfirmDel(null);
      load();
    } catch (e) {
      setErr(errMsg(e));
    }
  };

  const filtered = users.filter((u) => {
    if (!q.trim()) return true;
    const s = q.toLowerCase();
    return u.email?.toLowerCase().includes(s) || u.name?.toLowerCase().includes(s) || u.org_name?.toLowerCase().includes(s);
  });

  return (
    <div className="z-container" data-testid="admin-users-page">
      <h1 className="z-page-title">Utilisateurs</h1>
      <p className="z-page-sub">{view === "verifications" ? `${verifs.length} demande(s) de certification` : `${filtered.length} compte(s)`}</p>

      {/* View tabs */}
      <div style={{ display: "flex", gap: 6, marginBottom: 16 }}>
        <button className={`z-chip ${view === "users" ? "z-chip-active" : ""}`} onClick={() => setView("users")} data-testid="users-view-tab">Comptes</button>
        <button className={`z-chip ${view === "verifications" ? "z-chip-active" : ""}`} onClick={() => setView("verifications")} data-testid="verifications-view-tab">
          <ShieldCheck size={13} style={{ verticalAlign: "middle", marginRight: 4 }} /> Vérifications{verifs.length > 0 && view !== "verifications" ? ` (${verifs.length})` : ""}
        </button>
      </div>

      {err && <p className="z-error-text" data-testid="admin-users-error">{err}</p>}

      {view === "verifications" ? (
        loading ? (
          <div style={{ textAlign: "center" }}><span className="z-loader z-loader-dark" /></div>
        ) : verifs.length === 0 ? (
          <div className="z-card" style={{ padding: 24, textAlign: "center", color: "var(--z-muted)" }} data-testid="verifications-empty">
            Aucune demande de certification en attente.
          </div>
        ) : (
          <div style={{ display: "grid", gap: 12 }}>
            {verifs.map((v) => (
              <div key={v.user_id} className="z-card" style={{ padding: 16 }} data-testid={`verif-${v.user_id}`}>
                <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                  <span style={{ fontWeight: 700, fontSize: 16 }}>{v.org_name || v.name}</span>
                  <span className="z-chip" style={{ fontSize: 11 }}>{v.org_type}</span>
                  <span style={{ display: "inline-flex", alignItems: "center", gap: 4, color: "var(--z-orange)", fontSize: 12, fontWeight: 700 }}><Clock size={13} /> En attente</span>
                </div>
                <div style={{ fontSize: 13, color: "var(--z-muted)", margin: "4px 0 10px" }}>{v.name} · {v.email}{v.city ? ` · ${v.city}` : ""}</div>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, fontSize: 13, marginBottom: 10 }}>
                  <div><span style={{ color: "var(--z-muted)" }}>RCCM :</span> <strong>{v.rccm || "—"}</strong></div>
                  <div><span style={{ color: "var(--z-muted)" }}>NIF :</span> <strong>{v.nif || "—"}</strong></div>
                </div>
                {v.documents?.length > 0 && (
                  <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 12 }}>
                    {v.documents.map((d, i) => (
                      <a key={i} href={d.data_b64} target="_blank" rel="noreferrer" className="z-btn z-btn-outline z-btn-sm" data-testid={`verif-doc-${v.user_id}-${i}`}>
                        <FileText size={13} /> {d.name}
                      </a>
                    ))}
                  </div>
                )}
                <div style={{ display: "flex", gap: 8 }}>
                  <ZButton size="sm" loading={busy} onClick={() => approveVerif(v)} data-testid={`verif-approve-${v.user_id}`}>
                    <BadgeCheck size={14} /> Approuver
                  </ZButton>
                  <ZButton size="sm" variant="outline" onClick={() => { setRejectTarget(v); setRejectReason(""); }} data-testid={`verif-reject-${v.user_id}`}>
                    Rejeter
                  </ZButton>
                </div>
              </div>
            ))}
          </div>
        )
      ) : (
      <>
      <div style={{ position: "relative", marginBottom: 12 }}>
        <Search size={18} style={{ position: "absolute", left: 14, top: 14, color: "var(--z-muted)" }} />
        <input className="z-input" style={{ paddingLeft: 42 }} value={q} onChange={(e) => setQ(e.target.value)} placeholder="Rechercher nom, email, structure…" data-testid="admin-users-search" />
      </div>

      <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 18 }}>
        {ROLES.map((r) => (
          <button key={r.id || "all"} className={`z-chip ${role === r.id ? "z-chip-active" : ""}`} onClick={() => setRole(r.id)} data-testid={`role-filter-${r.id || "all"}`}>
            {r.label}
          </button>
        ))}
      </div>

      {loading ? (
        <div style={{ textAlign: "center" }}><span className="z-loader z-loader-dark" /></div>
      ) : (
        <div style={{ display: "grid", gap: 10 }}>
          {filtered.map((u) => (
            <div key={u.id} className="z-card" style={{ padding: 14, display: "flex", gap: 12, alignItems: "center", flexWrap: "wrap" }} data-testid={`admin-user-${u.id}`}>
              <div style={{ width: 42, height: 42, borderRadius: "50%", background: u.suspended ? "var(--z-muted)" : "var(--z-teal)", color: "white", display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 700 }}>
                {u.name?.[0]?.toUpperCase() || "?"}
              </div>
              <div style={{ flex: 1, minWidth: 200 }}>
                <div style={{ fontWeight: 700, display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                  {u.name}
                  <VerifiedBadge verified={u.verified} variant="pill" testId={`user-verified-${u.id}`} />
                  {u.verification_status === "pending" && <span className="z-badge" style={{ background: "rgba(218,113,1,0.12)", color: "var(--z-orange)" }}>Certif. en attente</span>}
                  {u.suspended && <span className="z-badge" style={{ background: "rgba(161,44,123,0.12)", color: "var(--z-error)" }}>Suspendu</span>}
                  {u.id === me?.id && <span className="z-badge z-badge-teal">Vous</span>}
                </div>
                <div style={{ fontSize: 13, color: "var(--z-muted)" }}>{u.email}</div>
                <div style={{ fontSize: 12, color: "var(--z-muted)", marginTop: 2 }}>
                  Rôle : <strong>{u.role}</strong>
                  {u.org_name && ` · ${u.org_name}`}
                  {u.city && ` · ${u.city}`}
                </div>
              </div>
              {u.id !== me?.id && (
                <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                  {u.role === "organizer" && (["association", "entreprise", "institution"].includes(u.org_type) || u.verified) && (
                    <button
                      onClick={() => toggleVerified(u)}
                      className="z-btn z-btn-outline z-btn-sm"
                      data-testid={`user-verify-${u.id}`}
                      title={u.verified ? "Retirer la certification" : "Certifier"}
                      style={u.verified ? { color: "var(--z-error)" } : { color: "var(--z-teal)" }}
                    >
                      {u.verified ? <><X size={14} /> Retirer certif.</> : <><BadgeCheck size={14} /> Certifier</>}
                    </button>
                  )}
                  <button
                    onClick={() => toggleSuspend(u)}
                    className="z-btn z-btn-outline z-btn-sm"
                    data-testid={`user-suspend-${u.id}`}
                    title={u.suspended ? "Réactiver" : "Suspendre"}
                  >
                    {u.suspended ? <><Check size={14} /> Réactiver</> : <><Ban size={14} /> Suspendre</>}
                  </button>
                  <button onClick={() => setConfirmDel(u)} className="z-btn z-btn-ghost z-btn-sm" style={{ color: "var(--z-error)" }} data-testid={`user-delete-${u.id}`}>
                    <Trash2 size={14} />
                  </button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
      </>
      )}

      <Modal open={!!rejectTarget} onClose={() => setRejectTarget(null)} title="Rejeter la demande" testId="verif-reject-modal">
        <p style={{ marginBottom: 12, fontSize: 14, color: "var(--z-muted)" }}>
          Indiquez le motif du refus pour <strong>{rejectTarget?.org_name || rejectTarget?.name}</strong> (envoyé par e-mail).
        </p>
        <ZInput label="Motif" value={rejectReason} onChange={(e) => setRejectReason(e.target.value)} placeholder="Ex : documents illisibles" data-testid="verif-reject-reason" />
        <div style={{ display: "flex", gap: 10, marginTop: 8 }}>
          <ZButton variant="outline" block onClick={() => setRejectTarget(null)} data-testid="verif-reject-cancel">Annuler</ZButton>
          <ZButton variant="danger" block loading={busy} onClick={rejectVerif} data-testid="verif-reject-confirm">Rejeter</ZButton>
        </div>
      </Modal>

      <Modal open={!!confirmDel} onClose={() => setConfirmDel(null)} title="Supprimer ce compte ?" testId="admin-user-delete-modal">
        <p style={{ marginBottom: 18 }}>
          <strong>{confirmDel?.name}</strong> ({confirmDel?.email}) sera supprimé(e) définitivement.
        </p>
        <div style={{ display: "flex", gap: 10 }}>
          <ZButton variant="outline" block onClick={() => setConfirmDel(null)} data-testid="admin-user-delete-cancel">Annuler</ZButton>
          <ZButton variant="danger" block onClick={remove} data-testid="admin-user-delete-confirm">Supprimer</ZButton>
        </div>
      </Modal>
    </div>
  );
}
